import React, { useRef, useState } from 'react';
import { connect } from 'react-redux';
import axios from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import DataTable from 'react-data-table-component';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { CSVLink } from 'react-csv';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

import { API_BASE_URL } from '../../config.js';
import FeedbackChatBox from './FeedbackChatBox';

// xin_chat_history.reason is varchar(200)
const MESSAGE_MAX_LENGTH = 200;

const today = () => new Date().toLocaleDateString('en-CA');

const FeedbackView = ({ fixNavbar }) => {
  const [filters, setFilters] = useState({
    start_date: today(),
    end_date: today(),
    status: 'ALL',
  });

  const [data, setData] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [fetched, setFetched] = useState(false);

  /* ================= CHAT STATE ================= */
  const [chatRow, setChatRow] = useState(null);
  const [chatThread, setChatThread] = useState(null);
  const [chatLoading, setChatLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [replyStatus, setReplyStatus] = useState('1');
  const [sending, setSending] = useState(false);
  const sendingRef = useRef(false);

  const authHeader = {
    headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` },
  };

  /* ================= FETCH DATA ================= */
  const loadData = (e) => {
    if (e) e.preventDefault();

    if (filters.end_date < filters.start_date) {
      toast.error('To Date cannot be before From Date', { toastId: 'fv-validation' });
      return;
    }

    setLoading(true);
    axios
      .get(API_BASE_URL + '/cfo_feedback_list', {
        ...authHeader,
        params: filters,
      })
      .then((res) => {
        setData(res.data || []);
        setFetched(true);
      })
      .catch((err) => {
        setData([]);
        setFetched(true);
        toast.error(
          err.response && err.response.status === 403
            ? 'You are not allowed to view this'
            : 'Failed to load feedback'
        );
      })
      .finally(() => setLoading(false));
  };

  /* ================= SEARCH ================= */
  const filteredData = data.filter((item) =>
    `${item.emp_code}${item.employee_name}${item.category_name}${item.reason}${item.applied_on}`
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
  );

  /* ================= EXPORT ================= */
  // The shared DownloadButtons component has its PDF button commented out and
  // a hard-coded title, so this screen builds its own pair.
  const exportRows = filteredData.map((row, index) => ({
    'S.No': index + 1,
    'Employee Name': row.employee_name,
    'Employee ID': row.emp_code,
    'Category Name': row.category_name,
    Reason: row.reason,
    'Mgmt Status': row.is_closed ? 'Closed' : 'Open',
    'Applied On': row.applied_on,
  }));

  const exportPDF = () => {
    if (!exportRows.length) {
      toast.error('No data to export', { toastId: 'fv-export' });
      return;
    }

    const doc = new jsPDF();
    doc.text('Employee Feedback View', 14, 12);
    doc.setFontSize(10);
    doc.text(`${filters.start_date} to ${filters.end_date}`, 14, 18);

    const columns = Object.keys(exportRows[0]);
    autoTable(doc, {
      head: [columns],
      body: exportRows.map((row) => columns.map((col) => row[col])),
      startY: 24,
      styles: { fontSize: 8, cellWidth: 'wrap' },
      columnStyles: { 4: { cellWidth: 60 } },
    });

    doc.save('employee_feedback_view.pdf');
  };

  /* ================= CHAT HANDLERS ================= */
  const loadThread = (id) => {
    setChatLoading(true);
    axios
      .get(API_BASE_URL + '/cfo_feedback_chat/' + id, authHeader)
      .then((res) => setChatThread(res.data))
      .catch(() => {
        toast.error('Failed to load conversation');
        setChatThread(null);
      })
      .finally(() => setChatLoading(false));
  };

  const openChat = (row) => {
    setChatRow(row);
    setChatThread(null);
    setMessage('');
    setReplyStatus('1');
    loadThread(row.id);
  };

  const closeChat = () => {
    setChatRow(null);
    setChatThread(null);
    setMessage('');
  };

  const handleSend = (e) => {
    e.preventDefault();

    // guard against double / rapid clicks while a send is already in flight
    if (sendingRef.current) return;

    if (!message.trim()) {
      toast.error('Kindly enter the message', { toastId: 'fv-chat' });
      return;
    }

    sendingRef.current = true;
    setSending(true);
    axios
      .post(
        API_BASE_URL + '/cfo_feedback_reply',
        { feedback_id: chatRow.id, reason: message, status: replyStatus },
        authHeader
      )
      .then((res) => {
        if (res.data && res.data.status === 200) {
          toast.success(res.data.message);
          setMessage('');
          loadThread(chatRow.id);
          // the row's status and unread count have both moved on
          loadData();
        } else {
          toast.error((res.data && res.data.message) || 'Failed to send', {
            toastId: 'fv-chat',
          });
        }
      })
      .catch(() => toast.error('Failed to send reply', { toastId: 'fv-chat' }))
      .finally(() => {
        sendingRef.current = false;
        setSending(false);
      });
  };

  /* ================= DATATABLE COLUMNS ================= */
  const columns = [
    {
      name: 'Action',
      width: '90px',
      cell: (row) =>
        // a closed feedback has no chat button, same as the legacy module
        row.is_closed ? (
          <span className="text-muted">-</span>
        ) : (
          <button
            type="button"
            className="btn btn-sm btn-outline-info"
            title="Chat"
            onClick={() => openChat(row)}
          >
            <i className="fa fa-comments" />
          </button>
        ),
    },
    {
      name: 'Employee Name',
      selector: (row) => row.employee_name,
      sortable: true,
      wrap: true,
      grow: 2,
      minWidth: '170px',
      cell: (row) => (
        <div>
          <div>{row.employee_name}</div>
          <small className="text-muted">{row.emp_code}</small>
        </div>
      ),
    },
    {
      name: 'Category Name',
      selector: (row) => row.category_name || '-',
      sortable: true,
      wrap: true,
      grow: 2,
      minWidth: '200px',
    },
    {
      name: 'Reason',
      selector: (row) => row.reason || '',
      sortable: true,
      wrap: true,
      grow: 3,
      cell: (row) => {
        const reason = row.reason || '';
        const shortText =
          reason.length > 40 ? reason.substring(0, 40) + '...' : reason;
        return (
          <div className="remark-hover-wrapper">
            {shortText}
            <div className="remark-hover-box gp-scroll gp-left">{reason}</div>
          </div>
        );
      },
    },
    {
      name: 'Mgmt Status',
      selector: (row) => row.is_closed,
      sortable: true,
      width: '140px',
      cell: (row) =>
        row.is_closed ? (
          <span className="badge badge-danger">Closed</span>
        ) : (
          <span className="badge badge-success">
            Open
            {row.unread_count > 0 && (
              <span className="badge badge-light ml-1">{row.unread_count}</span>
            )}
          </span>
        ),
    },
    {
      name: 'Applied On',
      selector: (row) => row.applied_on || '-',
      sortable: true,
      minWidth: '130px',
    },
  ];

  return (
    <>
      <ToastContainer position="bottom-right" />
      <div className={`section-body ${fixNavbar ? 'marginTop' : ''}`}>
        <div className="container-fluid">
          {/* ================= FILTERS ================= */}
          <div className="card">
            <div className="card-header">
              <h3
                className="card-title d-flex align-items-center mb-0"
                style={{ color: '#1d3a8a', fontWeight: 600 }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="22"
                  height="22"
                  viewBox="0 0 16 16"
                  fill="#1d3a8a"
                  style={{ marginRight: '8px', verticalAlign: '-3px' }}
                >
                  <path d="M14 1a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1H4.414A2 2 0 0 0 3 11.586l-2 2V2a1 1 0 0 1 1-1zM2 0a2 2 0 0 0-2 2v12.793a.5.5 0 0 0 .854.353l2.853-2.853A1 1 0 0 1 4.414 12H14a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2z" />
                </svg>
                Employee Feedback View
              </h3>
            </div>

            <form className="card-body" onSubmit={loadData}>
              <div className="row">
                <div className="col-md-3 form-group">
                  <label>From Date</label>
                  <DatePicker
                    selected={
                      filters.start_date ? new Date(filters.start_date) : null
                    }
                    onChange={(date) => {
                      const value = date ? date.toLocaleDateString('en-CA') : '';
                      setFilters((prev) => ({
                        ...prev,
                        start_date: value,
                        end_date:
                          prev.end_date && value && prev.end_date < value
                            ? value
                            : prev.end_date,
                      }));
                    }}
                    dateFormat="yyyy-MM-dd"
                    placeholderText="Select From Date"
                    className="form-control required-input"
                  />
                </div>

                <div className="col-md-3 form-group">
                  <label>To Date</label>
                  <DatePicker
                    selected={
                      filters.end_date ? new Date(filters.end_date) : null
                    }
                    onChange={(date) => {
                      const value = date ? date.toLocaleDateString('en-CA') : '';
                      setFilters((prev) => ({ ...prev, end_date: value }));
                    }}
                    dateFormat="yyyy-MM-dd"
                    placeholderText="Select To Date"
                    className="form-control required-input"
                    minDate={
                      filters.start_date ? new Date(filters.start_date) : null
                    }
                  />
                </div>

                <div className="col-md-3 form-group">
                  <label>Status</label>
                  <select
                    className="form-control"
                    value={filters.status}
                    onChange={(e) =>
                      setFilters((prev) => ({ ...prev, status: e.target.value }))
                    }
                  >
                    <option value="ALL">Both</option>
                    <option value="1">Open</option>
                    <option value="2">Close</option>
                  </select>
                </div>

                <div className="col-md-3 form-group d-flex align-items-end">
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={loading}
                  >
                    {loading ? 'Loading...' : 'Get'}
                  </button>
                </div>
              </div>
            </form>
          </div>

          {/* ================= RESULTS ================= */}
          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center flex-wrap">
              <div className="d-flex align-items-center" style={{ gap: '8px' }}>
                <CSVLink
                  data={exportRows}
                  filename="employee_feedback_view.csv"
                  className="btn btn-sm btn-outline-secondary"
                >
                  CSV
                </CSVLink>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={exportPDF}
                >
                  PDF
                </button>
              </div>

              <div style={{ position: 'relative', width: '260px' }}>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Search"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{ paddingRight: '30px' }}
                />
                {searchTerm && (
                  <span
                    onClick={() => setSearchTerm('')}
                    style={{
                      position: 'absolute',
                      top: '50%',
                      right: '10px',
                      transform: 'translateY(-50%)',
                      cursor: 'pointer',
                      color: '#aaa',
                      fontSize: '18px',
                    }}
                  >
                    ✖
                  </span>
                )}
              </div>
            </div>

            <div className="card-body">
              <DataTable
                columns={columns}
                data={filteredData}
                progressPending={loading}
                pagination
                paginationRowsPerPageOptions={[10, 25, 50, 100]}
                highlightOnHover
                striped
                responsive
                persistTableHead
                noDataComponent={
                  fetched
                    ? 'No data available in table'
                    : 'Choose a date range and press Get'
                }
              />
            </div>
          </div>
        </div>
      </div>

      {/* ================= CHAT MODAL ================= */}
      {chatRow && (
        <>
          <div className="modal fade show d-block" role="dialog" tabIndex="-1">
            <div className="modal-dialog modal-lg" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Chat box</h5>
                  <button type="button" className="close" onClick={closeChat}>
                    <span>&times;</span>
                  </button>
                </div>

                <div className="modal-body">
                  <FeedbackChatBox
                    thread={chatThread}
                    loading={chatLoading}
                    viewer="cfo"
                    footer={
                      chatThread && chatThread.feedback.is_closed ? (
                        <div className="efb-closed-note">
                          This feedback is closed.
                        </div>
                      ) : (
                        <form className="efb-composer" onSubmit={handleSend}>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Type your message..."
                            maxLength={MESSAGE_MAX_LENGTH}
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            disabled={sending}
                          />
                          <select
                            className="form-control"
                            style={{ width: '140px', flex: '0 0 140px' }}
                            value={replyStatus}
                            onChange={(e) => setReplyStatus(e.target.value)}
                            disabled={sending}
                          >
                            <option value="1">chatting</option>
                            <option value="2">Close</option>
                          </select>
                          <button type="submit" className="btn" disabled={sending}>
                            {sending ? 'Sending...' : 'Send'}
                          </button>
                        </form>
                      )
                    }
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" />
        </>
      )}
    </>
  );
};

const mapStateToProps = (state) => ({
  fixNavbar: state.settings.isFixNavbar,
});

const mapDispatchToProps = () => ({});

export default connect(mapStateToProps, mapDispatchToProps)(FeedbackView);
